import React, {
  useEffect, useMemo, useRef,
} from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { ActionButton } from '../common/petCloud/ActionButton';

import { formatShortDateByLanguage } from '../../services/language.utils';
import { PetsAddedInPeriodSection } from './PetsAddedInPeriodSection';
import { PetsIncludedInPeriodSection } from './PetsIncludedInPeriodSection';
import { PetsRolledOverSection } from './PetsRolledOverSection';

import styles from './CompletePetDetails.module.css';

const CompletePetDetails = ({
  coveragePeriodSelected = [],
  filledFromRolloverPets = false,
  isPostSubmissionMode = false,
  onCompletePetInfoContinuePress = () => { },
  onCancelReplacement = () => { },
  onConfirmReplacement = () => { },
  onGoBackClick = () => { },
  onReplaceSubmit = () => { },
  onUndoReplace = () => { },
  onUpdatePetDemographics = () => { },
  petDemographics = [],
  getOriginalPetName = () => '',
  isReplaced = () => false,
}) => {
  const { t } = useTranslation('petDemos');

  const isPetDetailsComplete = (dependentData) => {
    const values = Object.values(dependentData);
    if (!values.length) {
      return false;
    }

    return values.every((value) => String(value).trim() !== '');
  };

  // Keep each pet's original array index
  // so edits still target the correct pet in petDemographics.
  const visiblePets = useMemo(
    () => petDemographics.map((pet, index) => ({ index, pet })),
    [petDemographics],
  );

  // Nothing left to fill in for this period — skip straight past this
  // screen instead of showing an empty pet list with just a Continue
  // button. Gated on the selected period's own pet slots (available
  // immediately) rather than petDemographics/visiblePets, which can still
  // be empty for a brief render right after switching periods, before the
  // parent hook's fill effect has populated it.
  const autoSkipGroupIdsRef = useRef(new Set());

  useEffect(() => {
    const groupId = coveragePeriodSelected?.groupId;
    const selectedPets = coveragePeriodSelected?.pets;

    if (groupId && Array.isArray(selectedPets) && selectedPets.length === 0
      && !isPostSubmissionMode
      && !autoSkipGroupIdsRef.current.has(groupId)) {
      autoSkipGroupIdsRef.current.add(groupId);
      onCompletePetInfoContinuePress();
    }
  }, [
    coveragePeriodSelected?.groupId,
    coveragePeriodSelected?.pets?.length,
    isPostSubmissionMode,
    onCompletePetInfoContinuePress,
  ]);

  // Based on what's actually shown: a period with nothing left to display
  // (e.g. every pet auto-resolved by rollover) has nothing to complete.
  const allPetsCompleted = useMemo(() => visiblePets.every(
    ({ pet }) => isPetDetailsComplete(pet.demographicData || {}),
  ), [visiblePets]);

  const { petsAddedInPeriod, petsRolledOver } = useMemo(() => {
    if (!filledFromRolloverPets) {
      return { petsAddedInPeriod: [], petsRolledOver: [] };
    }

    const added = [];
    const rolled = [];

    visiblePets.forEach((petItem) => {
      // Split by origin: rollover vs added this period.
      if (petItem.pet.isRolloverPet) {
        rolled.push(petItem);
      } else {
        added.push(petItem);
      }
    });

    return { petsAddedInPeriod: added, petsRolledOver: rolled };
  }, [visiblePets, filledFromRolloverPets]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('completeInformation.title')}</h2>

        <h5 className={styles.coveragePeriod}>
          <Trans
            components={{ boldBlue: <span className={styles.periodDate} /> }}
            i18nKey="completeInformation.coveragePeriod"
            ns="petDemos"
            values={{
              endDate: formatShortDateByLanguage(
                coveragePeriodSelected.coverageEndDate,
              ),
              startDate: formatShortDateByLanguage(
                coveragePeriodSelected.coverageStartDate,
              ),
            }}
          />
        </h5>
      </div>

      <hr className={styles.headerDivider} />

      {filledFromRolloverPets && !isPostSubmissionMode ? (
        <>
          <PetsAddedInPeriodSection
            isPetDetailsComplete={isPetDetailsComplete}
            onUpdatePetDemographics={onUpdatePetDemographics}
            pets={petsAddedInPeriod}
          />

          <PetsRolledOverSection
            getOriginalPetName={getOriginalPetName}
            isPetDetailsComplete={isPetDetailsComplete}
            isReplaced={isReplaced}
            onCancelReplacement={onCancelReplacement}
            onConfirmReplacement={onConfirmReplacement}
            onReplaceSubmit={onReplaceSubmit}
            onUndoReplace={onUndoReplace}
            onUpdatePetDemographics={onUpdatePetDemographics}
            pets={petsRolledOver}
          />
        </>
      ) : (
        <PetsIncludedInPeriodSection
          isPetDetailsComplete={isPetDetailsComplete}
          isPostSubmissionMode={isPostSubmissionMode}
          onUpdatePetDemographics={onUpdatePetDemographics}
          pets={visiblePets}
        />
      )}

      <hr className={styles.headerDivider} />

      <div className={styles.buttonsContainer}>
        <ActionButton
          className={styles.actionButton}
          isSecondaryButton
          noMargin
          onClick={onGoBackClick}
          title={t('goBack')}
        />

        <div
          className={styles.continueButtonWrapper}
          data-disabled={!allPetsCompleted}
        >
          <ActionButton
            className={styles.actionButton}
            disabled={!allPetsCompleted}
            noMargin
            onClick={onCompletePetInfoContinuePress}
            title={t('continue')}
          />

          {!allPetsCompleted ? (
            <span className={styles.continueTooltip}>
              {t('completeInformation.disabledContinueTooltip')}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export { CompletePetDetails };
